import { Component, Input, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { getGroupRoleTypeLabel } from '../../constants/group-role.constant';
import { getPrivacyTypeLabel, PrivacyTypeEnum } from '../../constants/privacy.constant';
import { SuccessPopupComponent } from '../../libraries/popup/components/success-popup/success-popup.component';
import { IGroupModel } from '../../models/groups.model';
import { GroupsService } from '../../services/api/groups/groups.service';
import { GroupContextService } from '../../services/helper/group-context/group-context.service';
import { LoaderService } from '../../services/loader/loader.service';

@Component({
    selector: 'app-group',
    standalone: false,
    templateUrl: './group.component.html',
    styleUrl: './group.component.scss',
})
export class GroupComponent {
    @Input() isEditPin = false;
    @Input() isNotification = false;
    protected groupSignal = signal<IGroupModel>({} as IGroupModel);
    protected PrivacyTypeEnum = PrivacyTypeEnum;
    protected getPrivacyTypeLabel = getPrivacyTypeLabel;
    protected getGroupRoleTypeLabel = getGroupRoleTypeLabel;

    constructor(
        private dialog: MatDialog,
        private groupsService: GroupsService,
        private loaderService: LoaderService,
        private groupContextService: GroupContextService,
    ) {}

    @Input() set group(value: IGroupModel) {
        this.groupSignal.set(value);
    }

    protected acceptGroupInvite(group: IGroupModel): void {
        this.loaderService.show();
        this.groupsService.acceptGroupInvite(group.id).subscribe({
            next: () => {
                this.groupContextService.notifyGroupInvitesUpdated();
                this.groupSignal.update((g) => ({
                    ...g,
                    members: {
                        ...g.members,
                        total: (g.members?.total ?? 0) + 1,
                    },
                    joined: true,
                }));
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: 'Welcome to the family!',
                    message: `You’re now part of <strong>${group.name}</strong>. Jump in and start connecting with the community.`,
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                    confirmButtonLabel: 'Awesome',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Join group failed', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected declineGroupInvite(group: IGroupModel): void {
        this.loaderService.show();
        this.groupsService.rejectGroupInvite(group.id).subscribe({
            next: () => {
                this.groupContextService.notifyGroupInvitesUpdated();
                this.groupSignal.update((g) => ({
                    ...g,
                    joined: false,
                }));
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Decline group invite failed', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected joinGroup(group: IGroupModel): void {
        this.loaderService.show();
        this.groupsService.joinGroup(group.id).subscribe({
            next: () => {
                this.groupSignal.update((g) => ({
                    ...g,
                    members: {
                        ...g.members,
                        total: (g.members?.total ?? 0) + 1,
                    },
                    joined: true,
                }));
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: 'Welcome to the family!',
                    message: `You’re now part of <strong>${group.name}</strong>. Jump in and start connecting with the community.`,
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                    confirmButtonLabel: 'Awesome',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Join group failed', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected joinPrivateGroup(group: IGroupModel): void {
        if (group.requested) {
            return;
        }
        this.loaderService.show();
        this.groupsService.joinGroup(group.id).subscribe({
            next: () => {
                this.groupSignal.update((g) => ({
                    ...g,
                    requested: true,
                }));
                const popupConfig = SuccessPopupComponent.getDialogConfig();
                popupConfig.data = {
                    title: `Request Sent`,
                    message: `Thanks for stepping up! We've sent your request to join <strong>${group.name}</strong>. We'll notify you once you're approved.`,
                    iconPath: '/assets/images/shared/success-green-icon.svg',
                };
                this.dialog.open(SuccessPopupComponent, popupConfig);
            },
            error: (error) => {
                const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                errorPopupConfig.data = {
                    title: `Error`,
                    message: error.error?.message,
                };
                this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                console.error('Request to join group failed', error);
            },
            complete: () => {
                this.loaderService.hide();
            },
        });
    }

    protected togglePinGroup(group: IGroupModel): void {
        if (group.pinned) {
            this.groupsService.unpinGroup(group.id).subscribe({
                next: () => {
                    this.groupSignal.update((g) => ({
                        ...g,
                        pinned: false,
                    }));
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Unpin group failed', error);
                },
            });
        } else {
            this.groupsService.pinGroup(group.id).subscribe({
                next: () => {
                    this.groupSignal.update((g) => ({
                        ...g,
                        pinned: true,
                    }));
                },
                error: (error) => {
                    const errorPopupConfig = SuccessPopupComponent.getDialogConfig();
                    errorPopupConfig.data = {
                        title: `Error`,
                        message: error.error?.message,
                    };
                    this.dialog.open(SuccessPopupComponent, errorPopupConfig);
                    console.error('Pin group failed', error);
                },
            });
        }
    }
}
